import { useCallback } from 'react';

/**
 * Custom hook for authentication utilities
 */
export const useAuth = () => {
  /**
   * Validate email format
   */
  const validateEmail = useCallback((email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }, []);

  /**
   * Validate password strength
   */
  const validatePassword = useCallback((password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return {
      isValid: password.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar,
      errors: {
        length: password.length < minLength ? `Password must be at least ${minLength} characters` : null,
        uppercase: !hasUpperCase ? 'Password must contain at least one uppercase letter' : null,
        lowercase: !hasLowerCase ? 'Password must contain at least one lowercase letter' : null,
        numbers: !hasNumbers ? 'Password must contain at least one number' : null,
        special: !hasSpecialChar ? 'Password must contain at least one special character' : null
      }
    };
  }, []);

  /**
   * Sanitize input to prevent XSS attacks
   */
  const sanitizeInput = {
    email: (email) => {
      return email.trim().toLowerCase();
    },
    password: (password) => {
      return password.trim();
    },
    text: (text) => {
      return text.trim().replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    }
  };

  /**
   * Validate input with comprehensive checks
   */
  const validateInput = {
    email: validateEmail,
    password: validatePassword,
    required: (value) => {
      return value && value.trim().length > 0;
    },
    minLength: (value, min) => {
      return value && value.length >= min;
    },
    maxLength: (value, max) => {
      return value && value.length <= max;
    }
  };

  /**
   * Check if token is expired
   */
  const isTokenExpired = useCallback((tokenExpiry) => {
    if (!tokenExpiry) return true;
    return new Date() > new Date(tokenExpiry);
  }, []);

  /**
   * Get token from storage
   */
  const getStoredToken = useCallback(() => {
    try {
      const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');
      const expiresAt = localStorage.getItem('token_expires_at') || sessionStorage.getItem('token_expires_at');
      
      if (token && !isTokenExpired(expiresAt)) {
        return { token, expiresAt };
      }
      
      // Clear expired token
      if (token) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('token_expires_at');
        sessionStorage.removeItem('auth_token');
        sessionStorage.removeItem('token_expires_at');
      }
      
      return null;
    } catch (error) {
      console.error('Error getting stored token:', error);
      return null;
    }
  }, [isTokenExpired]);

  /**
   * Clear all authentication data
   */
  const clearAuthData = useCallback(() => {
    try {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('token_expires_at');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('token_expires_at');
    } catch (error) {
      console.error('Error clearing auth data:', error);
    }
  }, []);

  return {
    validateInput,
    sanitizeInput,
    isTokenExpired,
    getStoredToken,
    clearAuthData
  };
};
